import { TTheme, useCustomTheme } from '@/core/theme'
import { InfoBanner } from '@/ui/components/BannerAlert'
import { Label } from '@/ui/components/Label'
import Typography from '@/ui/components/typography'
import { AffectedAssetsTable } from '@ui/drawer/common/AffectedAssetsTable'
import { IssueDrawerLayout } from '@ui/drawer/common/IssueDrawerLayout'
import { IssueDrawerFooter } from '@ui/drawer/common/IssueDrawerFooter'
import { RemediationStepsCard } from '@ui/drawer/common/RemediationStepsCard'
import { useBecIssueDetails } from './useBecIssueDetails'
import { ExpandableRuleCard } from './components/ExpandableRuleCard'
import RuleInfo from './components/RuleInfo/RuleInfo'
import { RolesTitleWrapper, RulesWrapper } from './becIssueDetails.styles'
import WarningTriangleIcon from '@/assets/images/warning-triangle.svg?react'

const TAKEOVER_WARNING_TITLE =
    'WARNING: This activity could be part of an active attempt to account take-over.'
const TAKEOVER_WARNING_DESCRIPTION =
    'If you don’t recognize these rules - immediate action required.'

const BecIssueDetails = () => {
    const theme = useCustomTheme() as TTheme
    const {
        issue,
        rows,
        selectedIds,
        setSelectedIds,
        resolution,
        openRules,
        columns,
        selectedPayloads,
        selectionDisabled,
    } = useBecIssueDetails()

    const footer = issue.status === 'open' && (
        <IssueDrawerFooter
            resolution={resolution}
            selectedCount={selectedIds.length}
            hasSelectableAssets={rows.length > 0}
            selection={{ assets: selectedPayloads }}
        />
    )

    return (
        <IssueDrawerLayout footer={footer}>
            <Typography
                variant="s-regular"
                noWrap={false}
                color={theme.colors.greys[800]}
                whiteSpaceRule="pre-line"
                testId="bec-issue-short-description"
            >
                {issue.short_description}
            </Typography>
            <InfoBanner
                variant="error"
                dataTestId="bec-warning-banner"
                icon={
                    <WarningTriangleIcon
                        width={16}
                        height={16}
                        color={theme.colors.semantic.error}
                        data-testid="bec-warning-icon"
                    />
                }
                title={
                    <Typography
                        variant="s-bold"
                        color={theme.colors.semantic.bannerIconColor}
                    >
                        {TAKEOVER_WARNING_TITLE}
                    </Typography>
                }
                description={TAKEOVER_WARNING_DESCRIPTION}
            />
            <RemediationStepsCard
                issue={issue}
                title="Immediate actions are required:"
            />
            <RolesTitleWrapper>
                <Typography variant="s-regular" color={theme.colors.greys[800]}>
                    Suspicious rules for mailbox:
                </Typography>
                <Label
                    dataTestId="drawer-bec-issue-details-hostname"
                    title={issue.hostname}
                    variant="info"
                    textColor={theme.colors.greys[700]}
                />
            </RolesTitleWrapper>
            <RulesWrapper>
                {openRules.map((rule: TBECRule, ruleIndex: number) => (
                    <ExpandableRuleCard
                        key={`${rule.name}-${ruleIndex}`}
                        ruleName={rule.name}
                        defaultExpanded={ruleIndex === 0}
                        dataTestId={`drawer-bec-inbox-rule-${ruleIndex}-banner`}
                    >
                        <RuleInfo
                            conditions={rule.conditions ?? []}
                            actions={rule.actions ?? []}
                            dataTestId={`rule-info-${ruleIndex}`}
                        />
                    </ExpandableRuleCard>
                ))}
            </RulesWrapper>
            <AffectedAssetsTable
                testIdPrefix="bec-assets"
                rows={rows}
                columns={columns}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                disabled={selectionDisabled}
            />
        </IssueDrawerLayout>
    )
}

export default BecIssueDetails
